import { useLayoutEffect, useRef, useState } from "react";
import { formatDashboardCurrency } from "@/features/dashboard/metricFormatters";
import type {
  DashboardMoneySeriesPoint,
  DashboardPeriodMetadata,
} from "@/features/dashboard/types";
import type { DashboardPeriodState } from "@/features/dashboard/period";

const label = (point: DashboardMoneySeriesPoint) =>
  `${point.from === point.to ? point.to.slice(8, 10) + "/" + point.to.slice(5, 7) : point.from.slice(8, 10) + "/" + point.from.slice(5, 7) + "–" + point.to.slice(8, 10) + "/" + point.to.slice(5, 7)} / ${point.to.slice(0, 4)} · ${formatDashboardCurrency(point.value_cents).full}`;

const isoWeekStart = (day: string) => {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7));
  return date.getTime();
};

export function ClientTrend({
  points,
  period,
  metadata,
  onHistoryChange,
}: {
  points: DashboardMoneySeriesPoint[];
  period: DashboardPeriodState;
  metadata: DashboardPeriodMetadata;
  onHistoryChange: (history: 4 | 8 | 12) => void;
}) {
  const [active, setActive] = useState<string | null>(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [tooltipWidth, setTooltipWidth] = useState(180);
  const scrollRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const effectiveHistory = Math.min(period.history, metadata.available_history);
  const diff = (point: DashboardMoneySeriesPoint) => {
    if (metadata.history_unit === "month")
      return (
        (Number(metadata.to.slice(0, 4)) - Number(point.to.slice(0, 4))) * 12 +
        Number(metadata.to.slice(5, 7)) -
        Number(point.to.slice(5, 7))
      );
    const days =
      (Date.parse(`${metadata.to}T12:00:00Z`) -
        Date.parse(`${point.to}T12:00:00Z`)) /
      86400000;
    if (metadata.history_unit === "week")
      return Math.round(
        (isoWeekStart(metadata.to) - isoWeekStart(point.to)) / (86400000 * 7),
      );
    if (metadata.history_unit === "period" && metadata.from)
      return Math.round(
        (Date.parse(`${metadata.from}T12:00:00Z`) -
          Date.parse(`${point.from}T12:00:00Z`)) /
          (Date.parse(`${metadata.to}T12:00:00Z`) -
            Date.parse(`${metadata.from}T12:00:00Z`) +
            86400000),
      );
    return Math.round(days);
  };
  const max = Math.max(0, ...points.map((point) => point.value_cents));
  const plotted = points
    .flatMap((point) => {
      const slot = effectiveHistory - 1 - diff(point);
      return slot < 0 || slot >= effectiveHistory
        ? []
        : [
            {
              ...point,
              slot,
              x:
                effectiveHistory === 1
                  ? 50
                  : 6 + (slot * 88) / (effectiveHistory - 1),
              y: 85 - (point.value_cents / (max || 1)) * 69,
            },
          ];
    })
    .sort((a, b) => a.slot - b.slot);
  const runs: (typeof plotted)[] = [];
  plotted.forEach((point, index) => {
    if (!runs.length || point.slot !== plotted[index - 1]!.slot + 1)
      runs.push([]);
    runs.at(-1)!.push(point);
  });
  const curve = (run: typeof plotted) =>
    run.slice(1).reduce((path, point, index) => {
      const previous = run[index]!;
      const middle = (previous.x + point.x) / 2;
      return `${path} C ${middle} ${previous.y}, ${middle} ${point.y}, ${point.x} ${point.y}`;
    }, `M ${run[0]!.x} ${run[0]!.y}`);
  const selected = plotted.find(
    (point) => `${point.from}-${point.to}` === active,
  );
  useLayoutEffect(() => {
    if (selected && tooltipRef.current)
      setTooltipWidth(tooltipRef.current.offsetWidth);
  }, [selected?.from, selected?.to, selected?.value_cents]);
  const chart = chartRef.current;
  const scroll = scrollRef.current;
  const visibleStart =
    chart && scroll
      ? scrollLeft - parseFloat(getComputedStyle(chart).marginLeft)
      : 0;
  const tooltipLeft =
    chart && scroll && selected
      ? Math.max(
          Math.max(0, visibleStart + 10),
          Math.min(
            (selected.x / 100) * chart.clientWidth - tooltipWidth / 2,
            Math.max(
              0,
              Math.min(
                chart.clientWidth - tooltipWidth,
                visibleStart + scroll.clientWidth - tooltipWidth - 8,
              ),
            ),
          ),
        )
      : 0;
  const selectAt = (x: number, element: HTMLElement) => {
    const bounds = element.getBoundingClientRect();
    const slot = Math.max(
      0,
      Math.min(
        effectiveHistory - 1,
        Math.round(
          (((x - bounds.left) / bounds.width) * 100 - 6) *
            ((effectiveHistory - 1) / 88),
        ),
      ),
    );
    const point = plotted.find((candidate) => candidate.slot === slot);
    setActive(point ? `${point.from}-${point.to}` : null);
  };
  return (
    <section
      className="lnk209-panel lnk209-trend"
      aria-label="Evolução de compras"
      data-buckets={
        metadata.history_unit === "month"
          ? "meses"
          : metadata.history_unit === "week"
            ? "semanas"
            : metadata.history_unit === "day"
              ? "dias"
              : "intervalos"
      }
    >
      <header>
        <h2>Evolução de compras</h2>
        <div className="lnk209-trend-controls">
          <div className="lnk209-history" aria-label="Histórico da evolução">
            {([4, 8, 12] as const).map((history) => (
              <button
                key={history}
                aria-pressed={period.history === history}
                onClick={() => {
                  setActive(null);
                  onHistoryChange(history);
                }}
              >
                {history}
              </button>
            ))}
          </div>
        </div>
      </header>
      {plotted.length ? (
        <div
          className="lnk209-chart-scroll"
          ref={scrollRef}
          onScroll={(event) => setScrollLeft(event.currentTarget.scrollLeft)}
        >
          <div
            className="lnk209-chart-space"
            data-history={effectiveHistory}
            style={
              {
                "--lnk209-history-width": `${effectiveHistory * 50 + 30}px`,
              } as React.CSSProperties
            }
          >
            <div
              className="lnk209-chart"
              ref={chartRef}
              aria-label={`Compras observadas em ${plotted.length} pontos; selecione um ponto para detalhes`}
              onPointerMove={(event) =>
                selectAt(event.clientX, event.currentTarget)
              }
              onPointerDown={(event) =>
                selectAt(event.clientX, event.currentTarget)
              }
              onPointerLeave={() => {
                if (!document.activeElement?.matches(".lnk209-point"))
                  setActive(null);
              }}
              onBlurCapture={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node))
                  setActive(null);
              }}
            >
              <div className="lnk209-chart-guides">
                <span>{formatDashboardCurrency(max).full}</span>
                <span>{formatDashboardCurrency(Math.floor(max / 2)).full}</span>
                <span>R$ 0</span>
              </div>
              <svg
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <defs>
                  <linearGradient
                    id="lnk209-client-fill"
                    x1="0"
                    x2="0"
                    y1="0"
                    y2="1"
                  >
                    <stop stopColor="#d9a447" stopOpacity=".36" />
                    <stop offset="1" stopColor="#d9a447" stopOpacity="0" />
                  </linearGradient>
                </defs>
                {runs
                  .filter((run) => run.length > 1)
                  .map((run) => (
                    <path
                      key={run[0]!.from}
                      className="lnk209-area-fill"
                      d={`M ${run[0]!.x} 90 L ${curve(run).slice(2)} L ${run.at(-1)!.x} 90 Z`}
                      fill="url(#lnk209-client-fill)"
                    />
                  ))}
                <path
                  className="lnk209-area-path"
                  d={runs.map(curve).join(" ")}
                  fill="none"
                  stroke="#bd8730"
                  strokeWidth="2"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
              {selected && (
                <span
                  className="lnk209-hover-guide"
                  style={{ left: `${selected.x}%` }}
                  aria-hidden="true"
                />
              )}
              {plotted.map((point) => (
                <button
                  key={`${point.from}-${point.to}`}
                  className="lnk209-point"
                  style={{ left: `${point.x}%`, top: `${point.y}%` }}
                  aria-label={label(point)}
                  onMouseEnter={() => setActive(`${point.from}-${point.to}`)}
                  onFocus={() => setActive(`${point.from}-${point.to}`)}
                  onClick={() => setActive(`${point.from}-${point.to}`)}
                  onKeyDown={(event) => {
                    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight")
                      return;
                    const buttons = Array.from(
                      event.currentTarget.parentElement!.querySelectorAll<HTMLButtonElement>(
                        ".lnk209-point",
                      ),
                    );
                    const next =
                      buttons[
                        buttons.indexOf(event.currentTarget) +
                          (event.key === "ArrowLeft" ? -1 : 1)
                      ];
                    if (next) {
                      event.preventDefault();
                      next.focus();
                    }
                  }}
                >
                  <span />
                </button>
              ))}
              {selected && (
                <div
                  className="lnk209-chart-tooltip"
                  role="tooltip"
                  aria-label={label(selected)}
                  ref={tooltipRef}
                  style={{
                    left: tooltipLeft,
                    maxWidth:
                      chart && scroll
                        ? Math.max(
                            0,
                            Math.min(chart.clientWidth, scroll.clientWidth) -
                              20,
                          )
                        : undefined,
                  }}
                >
                  {label(selected)}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <p className="lnk209-empty">
          Ainda não há compras observadas para esta seleção.
        </p>
      )}
    </section>
  );
}
